import { useState, useEffect, useCallback, useRef, memo } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Box,
  Text,
  useToast,
  HStack,
  Spacer,
  Spinner
} from '@chakra-ui/react'
import { DownloadIcon, ExternalLinkIcon } from '@chakra-ui/icons'
import { HiPencilAlt } from 'react-icons/hi'
import { useMutateVisit } from '../../hooks/visits'
import Can from '../../auth/Can'
import config from '../../config'

const getApiBase = () => {
  if (process.env.REACT_APP_NODE_ENV === 'production') {
    return config.prod.API_BASE
  }
  if (process.env.REACT_APP_NODE_ENV === 'test') {
    return config.test.API_BASE
  }
  return config.dev.API_BASE
}

const isCertificateDocument = (url, documentName) => {
  const value = `${url || ''} ${documentName || ''}`.toLowerCase()
  return value.includes('certificado')
}

const getOrtFallbackUrl = (ortId, url, documentName) => {
  if (!ortId) return null
  const apiBase = getApiBase()
  if (isCertificateDocument(url, documentName)) {
    return `${apiBase}files/ort-certificate/${ortId}`
  }
  return `${apiBase}files/ort-pdf/${ortId}`
}

const getPdfFilenameFromUrl = (pdfUrl) => {
  if (!pdfUrl) return 'documento.pdf'
  try {
    const pathname = new URL(pdfUrl).pathname
    const filename = decodeURIComponent(pathname.split('/').pop() || '')
    return filename.endsWith('.pdf') ? filename : `${filename || 'documento'}.pdf`
  } catch {
    const filename = decodeURIComponent(pdfUrl.split('/').pop()?.split('?')[0] || '')
    return filename.endsWith('.pdf') ? filename : `${filename || 'documento'}.pdf`
  }
}

const encodePdfUrl = (pdfUrl) => {
  if (!pdfUrl) return ''
  const withoutHash = pdfUrl.split('#')[0]
  const [base, query = ''] = withoutHash.split('?')
  const lastSlash = base.lastIndexOf('/')
  if (lastSlash === -1) return encodeURI(pdfUrl)
  const originAndPath = base.slice(0, lastSlash + 1)
  const rawFilename = base.slice(lastSlash + 1)
  const encodedFilename = encodeURIComponent(decodeURIComponent(rawFilename))
  const querySuffix = query ? `?${query}` : ''
  return `${originAndPath}${encodedFilename}${querySuffix}`
}

// Hook personalizado para chequear soporte PDF
const usePDFSupport = (isOpen) => {
  const [canDisplayPDF, setCanDisplayPDF] = useState(true)

  useEffect(() => {
    if (!isOpen) return
    const nav = navigator
    if (nav.pdfViewerEnabled !== undefined) {
      setCanDisplayPDF(nav.pdfViewerEnabled)
    } else {
      const hasPDFSupport =
        navigator.mimeTypes && navigator.mimeTypes['application/pdf']
      setCanDisplayPDF(!!hasPDFSupport)
    }
  }, [isOpen])

  return canDisplayPDF
}

const PDFViewerModalComponent = ({
  isOpen,
  onClose,
  pdfUrl,
  documentName,
  ortId,
  userRole
}) => {
  const { mutate: signOrt, isLoading: signing } = useMutateVisit('SIGNATURE')
  const renderIdRef = useRef(0)
  renderIdRef.current += 1
  const toast = useToast()
  const canDisplayPDF = usePDFSupport(isOpen)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [previewUrl, setPreviewUrl] = useState('')
  const [currentPdfUrl, setCurrentPdfUrl] = useState(pdfUrl)
  const previewUrlRef = useRef(null)
  const lastPdfUrlRef = useRef(null)

  useEffect(() => {
    if (isOpen) {
      setCurrentPdfUrl(pdfUrl)
    }
  }, [pdfUrl, isOpen])

  const revokePreviewUrl = useCallback(() => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current)
      previewUrlRef.current = null
    }
  }, [])

  const loadPdfPreview = useCallback(async (url, options = {}) => {
    const { ortId: ortIdFallback, documentName: docName } = options

    const fetchPdfBlob = async (fetchUrl) => {
      const response = await fetch(fetchUrl)
      if (!response.ok) {
        throw new Error(`No se pudo cargar el PDF (${response.status})`)
      }
      const blob = await response.blob()
      if (blob.size < 100) {
        throw new Error('El archivo recibido no es un PDF válido')
      }
      revokePreviewUrl()
      const pdfBlob = blob.type.includes('pdf')
        ? blob
        : new Blob([blob], { type: 'application/pdf' })
      const objectUrl = URL.createObjectURL(pdfBlob)
      previewUrlRef.current = objectUrl
      setPreviewUrl(objectUrl)
    }

    try {
      await fetchPdfBlob(encodePdfUrl(url))
    } catch (primaryError) {
      const fallbackUrl = getOrtFallbackUrl(ortIdFallback, url, docName)
      if (fallbackUrl) {
        await fetchPdfBlob(fallbackUrl)
        return
      }
      throw primaryError
    }
  }, [revokePreviewUrl])

  // Cargar PDF como blob para evitar problemas de URL, sandbox y visores externos
  useEffect(() => {
    if (!isOpen) return
    if (!currentPdfUrl && !ortId) return

    const loadKey = currentPdfUrl || `ort-${ortId}-${documentName || 'documento'}`
    if (lastPdfUrlRef.current === loadKey && previewUrlRef.current) {
      setLoading(false)
      setLoadError(false)
      return
    }

    lastPdfUrlRef.current = loadKey
    let cancelled = false

    setLoading(true)
    setLoadError(false)
    setPreviewUrl('')

    loadPdfPreview(currentPdfUrl, { ortId, documentName })
      .then(() => {
        if (cancelled) return
        setLoading(false)
        setLoadError(false)
      })
      .catch((error) => {
        console.error('Error cargando PDF:', error)
        if (cancelled) return
        setLoading(false)
        setLoadError(true)
      })

    return () => {
      cancelled = true
    }
  }, [currentPdfUrl, isOpen, loadPdfPreview, ortId, documentName])

  // Reset cuando se cierra el modal
  useEffect(() => {
    if (!isOpen) {
      setLoading(true)
      setLoadError(false)
      setPreviewUrl('')
      lastPdfUrlRef.current = null
      revokePreviewUrl()
    }
  }, [isOpen, revokePreviewUrl])

  const handleDownload = useCallback(async () => {
    const filename = getPdfFilenameFromUrl(pdfUrl)
    const fallbackUrl = getOrtFallbackUrl(ortId, pdfUrl, documentName)
    const downloadUrl = pdfUrl ? encodePdfUrl(pdfUrl) : fallbackUrl

    const downloadBlob = async (fetchUrl) => {
      const response = await fetch(fetchUrl)
      if (!response.ok) {
        throw new Error('No se pudo descargar el archivo')
      }
      return response.blob()
    }

    try {
      let blob
      try {
        blob = await downloadBlob(downloadUrl)
      } catch (primaryError) {
        if (fallbackUrl && downloadUrl !== fallbackUrl) {
          blob = await downloadBlob(fallbackUrl)
        } else {
          throw primaryError
        }
      }
      const blobUrl = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(blobUrl)
      toast({
        title: 'Descarga iniciada',
        status: 'success',
        duration: 2000,
        isClosable: true
      })
    } catch (error) {
      console.error('Error en descarga:', error)
      const link = document.createElement('a')
      link.href = pdfUrl
      link.download = filename
      link.target = '_blank'
      link.rel = 'noopener noreferrer'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      toast({
        title: 'Descarga iniciada',
        status: 'success',
        duration: 2000,
        isClosable: true
      })
    }
  }, [pdfUrl, toast, ortId, documentName])

  const handleOpenInNewTab = useCallback(() => {
    if (previewUrlRef.current) {
      window.open(previewUrlRef.current, '_blank', 'noopener,noreferrer')
      return
    }
    window.open(encodePdfUrl(pdfUrl), '_blank', 'noopener,noreferrer')
  }, [pdfUrl])

  const handleRetry = useCallback(() => {
    if (!pdfUrl && !ortId) return
    lastPdfUrlRef.current = null
    revokePreviewUrl()
    setPreviewUrl('')
    setLoading(true)
    setLoadError(false)

    loadPdfPreview(pdfUrl, { ortId, documentName })
      .then(() => {
        setLoading(false)
        setLoadError(false)
      })
      .catch((error) => {
        console.error('Error en reintento:', error)
        setLoading(false)
        setLoadError(true)
      })
  }, [pdfUrl, loadPdfPreview, revokePreviewUrl, ortId, documentName])

  const handleSign = () => {
    if (!ortId) {
      toast({
        title: 'No se encontró el ID del documento para firmar',
        status: 'error',
        duration: 3000,
        isClosable: true
      })
      return
    }
    signOrt(
      { ort_id: ortId },
      {
        onSuccess: (response) => {
          const newUrl = response?.certificate_url
          if (newUrl) {
            lastPdfUrlRef.current = null
            setCurrentPdfUrl(newUrl)
          }
          toast({
            title: 'Documento firmado correctamente',
            status: 'success',
            duration: 2000,
            isClosable: true
          })
        },
        onError: (error) => {
          const message = error?.response?.data?.message
          toast({
            title:
              message === 'MISSING_SIGNATURE'
                ? 'Debe registrar su firma en el perfil antes de firmar'
                : 'Error al firmar el documento',
            status: 'error',
            duration: 4000,
            isClosable: true
          })
        }
      }
    )
  }
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="full"
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent
        m="auto"
        bg="white"
        maxW="95vw"
        maxH="95vh"
        borderRadius="md"
      >
        <ModalHeader bg="#F2F1FB" borderTopRadius="md">
          <HStack>
            <Text fontSize="18px" fontWeight="bold" color="#36355F">
              {documentName || 'Documento'}
            </Text>
            <Spacer />
          </HStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody p={0} h="85vh" overflow="hidden" bg="white">
          {pdfUrl ? (
            canDisplayPDF ? (
              <Box w="100%" h="85vh" minH="85vh" bg="white" position="relative">
                {/* Loading Overlay */}
                {loading && !loadError && (
                  <Box
                    position="absolute"
                    top="0"
                    left="0"
                    right="0"
                    bottom="0"
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    bg="rgba(255,255,255,0.98)"
                    zIndex={2}
                    gap={4}
                  >
                    <Spinner
                      size="xl"
                      color="#59BABA"
                      thickness="4px"
                      speed="0.65s"
                    />
                    <Text color="#666" fontSize="md" fontWeight="500">
                      Cargando documento...
                    </Text>
                    <Text color="#999" fontSize="sm">
                      Esto puede tomar unos segundos
                    </Text>
                  </Box>
                )}

                {/* Error Overlay */}
                {loadError && (
                  <Box
                    position="absolute"
                    top="0"
                    left="0"
                    right="0"
                    bottom="0"
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    bg="rgba(255,255,255,0.98)"
                    zIndex={3}
                    p={6}
                  >
                    <Box
                      bg="white"
                      p={8}
                      borderRadius="lg"
                      boxShadow="xl"
                      textAlign="center"
                      maxW="500px"
                    >
                      <Text
                        color="#E53E3E"
                        fontSize="2xl"
                        fontWeight="bold"
                        mb={3}
                      >
                        Error al cargar el documento
                      </Text>
                      <Text color="#666" fontSize="md" mb={6}>
                        El PDF no se pudo visualizar correctamente. Puedes
                        intentar de nuevo o usar las opciones alternativas.
                      </Text>
                      <HStack justify="center" spacing={3} flexWrap="wrap">
                        <Button
                          onClick={handleRetry}
                          bg="#59BABA"
                          color="white"
                          _hover={{ bg: '#4A9A9A' }}
                          size="md"
                        >
                          Reintentar
                        </Button>
                        <Button
                          onClick={handleOpenInNewTab}
                          colorScheme="teal"
                          variant="outline"
                          leftIcon={<ExternalLinkIcon />}
                          size="md"
                        >
                          Abrir en pestaña nueva
                        </Button>
                        <Button
                          onClick={handleDownload}
                          colorScheme="teal"
                          variant="outline"
                          leftIcon={<DownloadIcon />}
                          size="md"
                        >
                          Descargar
                        </Button>
                      </HStack>
                    </Box>
                  </Box>
                )}

                {previewUrl && !loadError && (
                  <embed
                    src={previewUrl}
                    type="application/pdf"
                    width="100%"
                    height="100%"
                    style={{ minHeight: '85vh', display: 'block' }}
                  />
                )}
              </Box>
            ) : (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                h="100%"
                bg="#F7F7F7"
                borderRadius="6px"
                gap={4}
                p={6}
              >
                <Text
                  color="#666"
                  fontSize="lg"
                  textAlign="center"
                  fontWeight="600"
                >
                  Tu navegador no puede mostrar PDFs directamente
                </Text>
                <Text color="#999" fontSize="md" textAlign="center">
                  Puedes descargar el archivo o abrirlo en una nueva pestaña
                </Text>
                <HStack spacing={3}>
                  <Button
                    onClick={handleOpenInNewTab}
                    bg="#59BABA"
                    color="white"
                    _hover={{ bg: '#4A9A9A' }}
                    leftIcon={<ExternalLinkIcon />}
                  >
                    Abrir en nueva pestaña
                  </Button>
                  <Button
                    onClick={handleDownload}
                    colorScheme="teal"
                    variant="outline"
                    leftIcon={<DownloadIcon />}
                  >
                    Descargar
                  </Button>
                </HStack>
              </Box>
            )
          ) : (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              h="100%"
              bg="#F7F7F7"
              borderRadius="6px"
            >
              <Text color="#666" fontSize="lg">
                No se pudo cargar el documento
              </Text>
            </Box>
          )}
        </ModalBody>

        <ModalFooter bg="#F2F1FB" borderBottomRadius="md">
          <Button
            bg="#F2F1FB"
            color="#59BABA"
            mr={3}
            onClick={onClose}
            border="1px"
            borderColor="#59BABA"
          >
            Cerrar
          </Button>
          {ortId && (
            <Can
              availableTo={[1, 3, 4, 5]} // Assuming roles 1,3,4,5 can sign, exclude 2
              yes={() => (
                <Button
                  bg="#F2F1FB"
                  color="#36355F"
                  mr={3}
                  leftIcon={<HiPencilAlt size={20} />}
                  border="1px"
                  borderColor="#59BABA"
                  onClick={handleSign}
                  _hover={{ bg: '#E6F7F7' }}
                  isDisabled={!pdfUrl || signing}
                  isLoading={signing}
                >
                  Firmar
                </Button>
              )}
              no={() => null}
            />
          )}

          <Button
            bg="#59BABA"
            color="white"
            onClick={handleDownload}
            _hover={{ bg: '#4A9A9A' }}
            leftIcon={<DownloadIcon />}
            isDisabled={!pdfUrl}
          >
            Descargar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

// Envolver con memo y comparador personalizado
const PDFViewerModal = memo(PDFViewerModalComponent, (prevProps, nextProps) => {
  // Retornar true si las props NO cambiaron (para NO re-renderizar)
  // Retornar false si las props cambiaron (para re-renderizar)
  const isEqual =
    prevProps.isOpen === nextProps.isOpen &&
    prevProps.pdfUrl === nextProps.pdfUrl &&
    prevProps.documentName === nextProps.documentName &&
    prevProps.ortId === nextProps.ortId

  return isEqual
})

PDFViewerModal.displayName = 'PDFViewerModal'

export default PDFViewerModal
